import React, {useEffect} from 'react';
import {over, preventDefault, useEvent, useUpdate, partialRight} from 'helpers';
import {AnyObject} from 'types';

interface Grabbed {
  clientX: number;
  clientY: number;
  offset: {x: number; y: number};
  origin: {x: number; y: number};
  pageX: number;
  pageY: number;
  target: EventTarget & HTMLElement; // Ensure target is an HTMLElement
  type?: string;
  id?: string | number;
  [key: string]: any;
}

export type DropCallback = (event: DragEvent, grabbed: Grabbed) => void;

type Condition = (grabbed: Grabbed) => boolean | null | undefined;

let grabbed: Grabbed | undefined;

const end = () => {
  if (grabbed) {
    grabbed.target.classList.remove('isPlaceholder');
    grabbed = undefined;
    window.removeEventListener('dragover', updateGrabbedPosition);
  }
};

const updateGrabbedPosition = (event: DragEvent) => {
  if (grabbed) {
    grabbed.clientX = event.clientX;
    grabbed.clientY = event.clientY;
    grabbed.pageX = event.pageX;
    grabbed.pageY = event.pageY;
  }
};

export const useDragStart = (props: AnyObject | undefined | false) => props && ({
  draggable: true,
  onDragEnd: end,
  onDragStart: (event: React.DragEvent<HTMLElement>) => {
    const {currentTarget, pageX, pageY, clientX, clientY, dataTransfer} = event;
    const {left, top} = currentTarget.getBoundingClientRect();
    event.stopPropagation();
    dataTransfer.effectAllowed = 'move';
    currentTarget.classList.add('isGrabbed');
    requestAnimationFrame(() => {
      currentTarget.classList.add('isPlaceholder');
      currentTarget.classList.remove('isGrabbed');
    });
    grabbed = {
      offset: {x: left - clientX, y: top - clientY},
      origin: {x: clientX, y: clientY},
      clientX,
      clientY,
      pageX,
      pageY,
      target: currentTarget,
      ...props
    };
    window.addEventListener('dragover', updateGrabbedPosition);
    window.dispatchEvent(new CustomEvent('grabbed', {detail: grabbed}));
  }
});

export const useGrabbed = () => {
  const update = useUpdate();
  useEvent('grabbed', update);
  useEvent('dragend', update);
  useEvent('drop', update);
  return grabbed;
};

export const useDrop = (callback: DropCallback, condition?: Condition) => {
  const currentGrabbed = useGrabbed();
  if (!condition || (currentGrabbed && condition(currentGrabbed))) {
    return {
      onDragOver: preventDefault,
      onDrop: over(partialRight(callback, currentGrabbed), end)
    };
  }
};

export const useGlobalDrop = (condition: Condition, callback: DropCallback) => {
  const currentGrabbed = useGrabbed();
  useEffect(() => {
    if (currentGrabbed && condition(currentGrabbed)) {
      const drop = partialRight(callback, currentGrabbed);
      window.addEventListener('dragover', preventDefault);
      window.addEventListener('drop', drop);
      return () => {
        window.removeEventListener('dragover', preventDefault);
        window.removeEventListener('drop', drop);
      };
    }
  }, [currentGrabbed]);
};
